export type AssistantPresetExpectedTool =
  | "search_availability"
  | "booking_lookup"
  | "create_booking"
  | "daily_briefing"
  | "attention_needed"
  | "upcoming_schedule"
  | "maintenance_report"
  | "utilization_report"
  | "profit_by_vehicle"
  | "revenue_forecast"
  | "cash_flow_summary"
  | "action_priority_queue"
  | "booking_gap_analysis"
  | "customer_churn_risk"
  | "expense_report"
  | "revenue_report"
  | "customer_insights"
  | "vehicles_returning_soon"
  | "generate_table"
  | "fleet_status_summary";

export type AssistantPresetExpectedResultType =
  | "availability"
  | "booking_list"
  | "booking_created"
  | "daily_briefing"
  | "attention_needed"
  | "upcoming_schedule"
  | "maintenance_list"
  | "utilization_report"
  | "profit_report"
  | "revenue_forecast"
  | "cash_flow"
  | "action_queue"
  | "gap_analysis"
  | "churn_risk"
  | "expense_report"
  | "revenue_report"
  | "customer_insights"
  | "returning_soon"
  | "table"
  | "status_summary";

export interface AssistantPresetDefinition {
  id: string;
  label: string;
  query: string;
  expectedTool: AssistantPresetExpectedTool;
  expectedResultType?: AssistantPresetExpectedResultType;
}

export const ASSISTANT_PRESET_DEFINITIONS = {
  dailyBriefing: {
    id: "dailyBriefing",
    label: "Daily Briefing",
    query: "Give me the daily briefing",
    expectedTool: "daily_briefing",
    expectedResultType: "daily_briefing",
  },
  actionList: {
    id: "actionList",
    label: "My Priorities",
    query: "Show my action list",
    expectedTool: "action_priority_queue",
    expectedResultType: "action_queue",
  },
  upcomingWeek: {
    id: "upcomingWeek",
    label: "Week Ahead",
    query: "Show upcoming schedule for the next 7 days",
    expectedTool: "upcoming_schedule",
    expectedResultType: "upcoming_schedule",
  },
  profitByVehicle: {
    id: "profitByVehicle",
    label: "Profit Report",
    query: "Which vehicle is most profitable?",
    expectedTool: "profit_by_vehicle",
    expectedResultType: "profit_report",
  },
  revenueForecast: {
    id: "revenueForecast",
    label: "Revenue Forecast",
    query: "Show revenue forecast for the next 30 days",
    expectedTool: "revenue_forecast",
    expectedResultType: "revenue_forecast",
  },
  cashFlow: {
    id: "cashFlow",
    label: "Cash Position",
    query: "Show cash flow summary",
    expectedTool: "cash_flow_summary",
    expectedResultType: "cash_flow",
  },
  bookingGaps: {
    id: "bookingGaps",
    label: "Calendar Gaps",
    query: "Show calendar gaps",
    expectedTool: "booking_gap_analysis",
    expectedResultType: "gap_analysis",
  },
  followUpList: {
    id: "followUpList",
    label: "Follow-Up List",
    query: "Show me the follow-up list",
    expectedTool: "customer_churn_risk",
    expectedResultType: "churn_risk",
  },
  returnsToday: {
    id: "returnsToday",
    label: "Today's Returns",
    query: "Which vehicles are returning today?",
    expectedTool: "vehicles_returning_soon",
    expectedResultType: "returning_soon",
  },
  overdueReturns: {
    id: "overdueReturns",
    label: "Overdue Returns",
    query: "Show me all overdue returns",
    expectedTool: "attention_needed",
    expectedResultType: "attention_needed",
  },
  maintenanceReport: {
    id: "maintenanceReport",
    label: "In Maintenance",
    query: "Show maintenance report",
    expectedTool: "maintenance_report",
    expectedResultType: "maintenance_list",
  },
  maintenanceReview: {
    id: "maintenanceReview",
    label: "Maintenance Review",
    query: "Show maintenance jobs pending review",
    expectedTool: "maintenance_report",
    expectedResultType: "maintenance_list",
  },
  activeBookings: {
    id: "activeBookings",
    label: "Active Bookings",
    query: "Show all active bookings",
    expectedTool: "booking_lookup",
    expectedResultType: "booking_list",
  },
  attentionNeeded: {
    id: "attentionNeeded",
    label: "Attention Needed",
    query: "Show attention needed",
    expectedTool: "attention_needed",
    expectedResultType: "attention_needed",
  },
  fleetSummary: {
    id: "fleetSummary",
    label: "Fleet Overview",
    query: "Give me a fleet status summary",
    expectedTool: "fleet_status_summary",
    expectedResultType: "status_summary",
  },
  createBooking: {
    id: "createBooking",
    label: "Create Booking",
    query: "Create a booking for a customer",
    expectedTool: "create_booking",
    expectedResultType: "booking_created",
  },
  availabilityToday: {
    id: "availabilityToday",
    label: "Available Now",
    query: "What is available today?",
    expectedTool: "search_availability",
    expectedResultType: "availability",
  },
  availabilityTable: {
    id: "availabilityTable",
    label: "Availability Table",
    query: "Show me the availability table",
    expectedTool: "generate_table",
    expectedResultType: "table",
  },
  expenseReport: {
    id: "expenseReport",
    label: "Expense Report",
    query: "Show our expense report this month",
    expectedTool: "expense_report",
    expectedResultType: "expense_report",
  },
  revenueReport: {
    id: "revenueReport",
    label: "Revenue Report",
    query: "Show revenue report for this month",
    expectedTool: "revenue_report",
    expectedResultType: "revenue_report",
  },
  utilizationReport: {
    id: "utilizationReport",
    label: "Utilization Report",
    query: "Show fleet utilization for this month",
    expectedTool: "utilization_report",
    expectedResultType: "utilization_report",
  },
  customerInsights: {
    id: "customerInsights",
    label: "Top Customers",
    query: "Show customer report",
    expectedTool: "customer_insights",
    expectedResultType: "customer_insights",
  },
} as const satisfies Record<string, AssistantPresetDefinition>;

export type AssistantPresetId = keyof typeof ASSISTANT_PRESET_DEFINITIONS;
export type AssistantPreset = (typeof ASSISTANT_PRESET_DEFINITIONS)[AssistantPresetId];

export const ASSISTANT_PRESETS = Object.values(ASSISTANT_PRESET_DEFINITIONS) as AssistantPreset[];

export const ASSISTANT_QUICK_PRESET_IDS = [
  "dailyBriefing",
  "actionList",
  "upcomingWeek",
  "profitByVehicle",
  "revenueForecast",
  "cashFlow",
  "bookingGaps",
  "followUpList",
  "returnsToday",
  "overdueReturns",
  "maintenanceReport",
  "activeBookings",
  "attentionNeeded",
  "fleetSummary",
  "createBooking",
  "availabilityToday",
] as const satisfies readonly AssistantPresetId[];

export const ASSISTANT_QUERY_PRESETS = Object.fromEntries(
  ASSISTANT_PRESETS.map((preset) => [preset.id, preset.query]),
) as { [K in AssistantPresetId]: (typeof ASSISTANT_PRESET_DEFINITIONS)[K]["query"] };

const LEGACY_ASSISTANT_QUERY_MAP: Record<string, AssistantPresetId> = {
  "What's my day look like?": "dailyBriefing",
  "What should I do today?": "actionList",
  "What's coming up this week?": "upcomingWeek",
  "What will I earn in the next 30 days?": "revenueForecast",
  "What's my cash position?": "cashFlow",
  "Which vehicles have idle time coming up?": "bookingGaps",
  "Which customers haven't booked recently?": "followUpList",
  "Show me all overdue returns": "overdueReturns",
  "Which cars are under maintenance?": "maintenanceReport",
  "Show me all active bookings": "activeBookings",
  "Any problems I should know about?": "attentionNeeded",
  "Book a vehicle for a customer": "createBooking",
  "What vehicles are available today?": "availabilityToday",
  "What is available today?": "availabilityToday",
  "How much did we make this month?": "revenueReport",
  "Fleet utilization this month": "utilizationReport",
  "Who are my best customers?": "customerInsights",
  "Show our P&L this month": "expenseReport",
  "Show all open maintenance jobs": "maintenanceReport",
  "Show maintenance jobs pending review": "maintenanceReview",
};

const ASSISTANT_PRESET_BY_QUERY = new Map<string, AssistantPreset>(
  ASSISTANT_PRESETS.map((preset) => [preset.query, preset]),
);

export function getAssistantPreset(id: AssistantPresetId): AssistantPreset {
  return ASSISTANT_PRESET_DEFINITIONS[id];
}

export function findAssistantPresetByQuery(query: string): AssistantPreset | null {
  const canonicalQuery = normalizeAssistantPresetQuery(query);
  return ASSISTANT_PRESET_BY_QUERY.get(canonicalQuery) ?? null;
}

export function normalizeAssistantPresetQuery(query: string): string {
  const trimmedQuery = query.trim();
  const presetId = LEGACY_ASSISTANT_QUERY_MAP[trimmedQuery];
  return presetId ? getAssistantPreset(presetId).query : trimmedQuery;
}
